import { INTRO } from 'Router/routes';
import { bootstrapKeycloakStore, keycloakStore } from 'hooks/zustandStore';
import { logger } from './logger';
import ValidationError, { FieldError } from './ValidationError';
import { getProcessEnvUrl } from './getUrl';

function parseResponseBody(response: Response) {
  const contentType = response.headers.get('Content-Type') ?? '';

  if (contentType.includes('text/html')) {
    return response.text();
  }

  if (contentType.includes('application/pdf')) {
    return response.blob();
  }

  return response.json();
}

export async function resolveIsLegacyLogin(): Promise<boolean> {
  const state = keycloakStore.getState();

  if (state.isLegacyLogin === null || state.isLegacyLoginLoading) {
    await bootstrapKeycloakStore();
  }

  return keycloakStore.getState().isLegacyLogin ?? true;
}

async function fetchData(
  path: string,
  customOptions: RequestInit = {},
  // params?: Record<string, string | number | boolean | undefined>
) {
  const isLegacyLogin = await resolveIsLegacyLogin();

  const defaultHeaders: Record<string, string> = {
    'Content-Type': 'application/json',
  };

  /* This will parse the params object to query parameters to format parameter=value, removing undefined fields.
  // Filter method removes potential undefined fields, but for some reasons the function returns [string]: string | undefined.
  // For that reason there is this ugly type assertion
  */

  // const searchString =
  //   params &&
  //   new URLSearchParams(
  //     Object.fromEntries(Object.entries(params).filter(([, v]) => v)) as Record<
  //       string,
  //       string
  //     >
  //   );

  // const formattedParams = searchString ? `?${searchString}` : '';

  const token = sessionStorage.getItem('token');

  if (isLegacyLogin && token) {
    defaultHeaders.Authorization = `Basic ${token}`;
  }

  const options: RequestInit = {
    ...customOptions,
    headers: {
      ...defaultHeaders,
      ...customOptions.headers,
    },
  };

  if (!isLegacyLogin) {
    options.credentials = 'include';
  }

  const baseUrl = isLegacyLogin
    ? getProcessEnvUrl('LEGACY_LOGIN_API')
    : `${getProcessEnvUrl('GATEWAY_URL')}/webapi`;

  const url = `${baseUrl}${path}`;

  if (customOptions.body instanceof FormData) {
    delete (options.headers as Record<string, string>)['Content-Type'];
  }

  const response = await fetch(url, options);

  if (response.status === 401) {
    sessionStorage.clear();

    window.location.replace(INTRO);
    return;
  }

  if (!response.ok) {
    try {
      const data = await response.clone().json();
      logger.error(`request: ${path}`, {
        request_url: url,
        error: data,
      });
    } catch (exception) {
      logger.error(
        `request: ${path} response parse`,
        {
          request_url: url,
          error: exception,
        },
        exception instanceof Error ? exception : undefined,
      );
    }
  }

  if (response.status === 400) {
    const { errors }: { errors: FieldError[] } = await response.json();
    throw new ValidationError(errors);
  }

  if (!response.ok) {
    throw new Error(response.status.toString());
  }

  try {
    return await parseResponseBody(response);
  } catch {
    // If response doesn't have content
    return undefined;
  }
}

export default fetchData;
